import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type * as React from 'react'

import { installInkWave } from '../../lib/signature/ink-wave-listeners'
import { cn } from '../../lib/utils'
import { PenProgress } from './pen-progress'

/**
 * DESIGN.md §5 "Buttons". Five variants, three heights and two icon sizes.
 *
 * - Shape: 4px corners; 36px standard, 28px compact, 44px large; 14px inline
 *   padding (10px compact); a 13px weight-500 label; a 16px icon, 8px gap.
 * - Primary is writing ink, one per group and last in it. It keeps its own
 *   fill on hover: the rising `--ink-press` wave is its hover.
 * - Secondary is paper with an `--input` boundary; outline is the same
 *   boundary on no fill; ghost has neither. All three hover to `--accent`
 *   under the rising ink.
 * - The ink wave (DESIGN.md "The ink wave", `lib/signature/ink-wave.ts`)
 *   rises through primary, secondary, outline and ghost on pointing and on
 *   press; never on keyboard focus, destructive, disabled or busy. Each
 *   variant sets the wave's colours in `--wave-ink` and `--wave-label`.
 * - Inside `data-surface="committed"` (the first-run deep ink surface) the
 *   variants take the on-deep colours and the `--brand-deep-ring` focus.
 * - Destructive has its own fill, label and hover.
 * - Focus is the shared 3px `--ring` at a 2px offset, so the paper gap
 *   carries it round a black or red fill. One ring for every variant.
 * - Press is a 1px drop. No scale, no shadow (The Two Elevations Rule).
 * - Disabled (`disabled` or `aria-disabled`) is recoloured with `--muted`,
 *   `--muted-foreground` and `--border`, never faded with opacity.
 * - Busy keeps its label and its colours; the progress stroke replaces any
 *   spinner, and the button cannot be pressed twice.
 *
 * `data-slot="button"`, `data-variant` and `data-size` are on the element,
 * which is `relative` so the `aria-hidden` wave layer can sit inside it, and
 * the busy stroke is `data-slot="button-progress"`.
 */
/** A paper button on the committed surface: deep ink, its text, the raised hover and the light wave. */
const ON_DEEP_PAPER = [
  'in-data-[surface=committed]:bg-transparent in-data-[surface=committed]:text-brand-deep-foreground',
  'in-data-[surface=committed]:not-disabled:hover:bg-brand-deep-raised',
  'in-data-[surface=committed]:[--wave-ink:var(--brand-deep-foreground)] in-data-[surface=committed]:[--wave-label:var(--brand-deep)]',
]

const buttonClasses = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap',
    'rounded-sm border text-label font-medium transition-ui',
    'not-disabled:not-aria-disabled:active:translate-y-px',
    'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
    'disabled:not-aria-busy:cursor-not-allowed aria-disabled:pointer-events-none aria-busy:cursor-progress',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    '[--wave-ink:var(--foreground)] [--wave-label:var(--background)]',
    'in-data-[surface=committed]:focus-visible:outline-brand-deep-ring',
  ],
  {
    variants: {
      variant: {
        primary: [
          'ink-wave-host border-primary bg-primary text-primary-foreground',
          '[--wave-ink:var(--ink-press)] [--wave-label:var(--primary-foreground)]',
          'in-data-[surface=committed]:border-brand-deep-foreground in-data-[surface=committed]:bg-brand-deep-foreground in-data-[surface=committed]:text-brand-deep',
          'in-data-[surface=committed]:[--wave-ink:var(--brand-deep-muted)] in-data-[surface=committed]:[--wave-label:var(--brand-deep)]',
        ],
        secondary: [
          'ink-wave-host border-input bg-card text-foreground',
          'not-disabled:hover:bg-accent',
          ON_DEEP_PAPER,
          'in-data-[surface=committed]:border-brand-deep-line',
        ],
        outline: [
          'ink-wave-host border-input bg-transparent text-foreground',
          'not-disabled:hover:bg-accent',
          ON_DEEP_PAPER,
          'in-data-[surface=committed]:border-brand-deep-line',
        ],
        ghost: [
          'ink-wave-host border-transparent bg-transparent text-foreground',
          'not-disabled:hover:bg-accent',
          ON_DEEP_PAPER,
        ],
        destructive: [
          'border-destructive bg-destructive text-destructive-foreground',
          'not-disabled:hover:border-destructive-hover not-disabled:hover:bg-destructive-hover',
        ],
      },
      size: {
        sm: 'h-control-compact px-2.5',
        md: 'h-control px-3.5',
        lg: 'h-control-large px-4',
        icon: 'size-control p-0',
        'icon-sm': 'size-control-compact p-0',
      },
    },
    compoundVariants: [
      // Disabled, but not busy: a busy button keeps its own colours so the
      // action it is running stays legible. Ghost stays unfilled.
      {
        variant: ['primary', 'secondary', 'outline', 'destructive'],
        class: [
          'disabled:not-aria-busy:border-border disabled:not-aria-busy:bg-muted disabled:not-aria-busy:text-muted-foreground',
          'aria-disabled:border-border aria-disabled:bg-muted aria-disabled:text-muted-foreground',
        ],
      },
      {
        variant: 'ghost',
        class: 'disabled:not-aria-busy:text-muted-foreground aria-disabled:text-muted-foreground',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

/**
 * The button classes. Anything styled with them, a link included, takes the
 * ink wave: the first call installs its document listeners, and `ink-wave-host`
 * marks the four variants it rises through.
 */
function buttonVariants(props?: Parameters<typeof buttonClasses>[0]) {
  installInkWave()
  return buttonClasses(props)
}

function Button({
  className,
  variant = 'primary',
  size = 'md',
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <PenProgress data-slot="button-progress" /> : null}
          {children}
        </>
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
